import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { Job } from 'models/response';

import selectors from 'state/selectors';
import services from 'services';
import utils from 'utils';

import { Wrapper, Title, Buttons } from '../../styles';

import CommonButton from 'components/Buttons/CommonButton';
import Table, { TableProps } from 'components/Table';

const Timers = () => {
  const t = utils.intl.useTranslator();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJob, setSelectedJob] = useState<Job | undefined>(undefined);
  const companies = useSelector(selectors.app.getCompanies);
  const [hasPermissions, setHasPermissions] = useState<boolean>(false);
  const { dateFormat, timeZone } = useSelector(selectors.user.getUserPreference);

  useEffect(() => {
    if (companies) {
      const companiesActions = companies.map((e) => {
        return e.actions;
      });
      const timerPrivCheck = companiesActions.map((e) => {
        const check = e.find((ele) => ele.type === 'TIMER_PRIV');
        return check?.active;
      });
      setHasPermissions(timerPrivCheck.every(Boolean));
    }
  }, [companies]);

  const columns: TableProps['columns'] = [
    {
      id: 'originalName',
      Header: t('name'),
      accessor: 'name',
      filterType: 'free',
    },
    {
      id: 'localizedName',
      Header: t('jobsName'),
      accessor: 'name',
      filterType: 'free',
      hasLocalKeys: ['LocalizableStringFe', 'monitorTimers'],
    },
    {
      Header: t('state'),
      accessor: 'state',
      filterType: 'free',
    },
    {
      Header: t('scheduleTime'),
      accessor: 'scheduleTime',
      filterType: 'date',
      dateFormat,
      filter: utils.date.epochFilterFunction(timeZone),
      Cell: ({ value }: { value: string }) => utils.date.convertDate(Number(value), timeZone),
    },
  ];

  const getListOfJobs = async () => {
    const { data } = await services.getListOfJobs();
    setJobs(data);
  };

  useEffect(() => {
    try {
      getListOfJobs();
    } catch (e) {
      console.error(e);
    }
  }, []);

  const onSelection = (selection: Job[]) => {
    setSelectedJob(selection[0]);
  };

  const resumeJob = async () => {
    if (selectedJob) {
      try {
        const { data } = await services.resumeJob(selectedJob.name);
        setSelectedJob(data);
        utils.app.notify('success', t('job_resumed'));
        getListOfJobs();
      } catch (e) {
        utils.app.notify('fail', t('job_not_resumed'));
        console.error(e);
      }
    }
  };

  const pauseJob = async () => {
    if (selectedJob) {
      try {
        const { data } = await services.pauseJob(selectedJob.name);
        setSelectedJob(data);
        utils.app.notify('success', t('job_paused'));
        getListOfJobs();
      } catch (e) {
        utils.app.notify('fail', t('job_not_pause'));
        console.error(e);
      }
    }
  };

  const runJob = async () => {
    if (selectedJob) {
      try {
        await services.runJob(selectedJob.name);
        utils.app.notify('success', t('job_run'));
        getListOfJobs();
      } catch (e) {
        utils.app.notify('fail', t('job_not_run'));
        console.error(e);
      }
    }
  };

  return (
    <Wrapper>
      <Title>
        <h2>{t('timers_title')}</h2>
      </Title>
      <Buttons>
        <CommonButton
          id="timers-resume-job-btn"
          scope="secondary"
          value={t('resumeJob')}
          type="button"
          action={() => resumeJob()}
          disabled={!selectedJob || selectedJob?.state === 'ENABLED' || !hasPermissions}
        />
        <CommonButton
          id="timers-pause-job-btn"
          scope="secondary"
          value={t('pauseJob')}
          type="button"
          action={() => pauseJob()}
          disabled={!selectedJob || selectedJob?.state === 'PAUSED' || !hasPermissions}
        />
        <CommonButton
          id="timers-run-job-btn"
          scope="secondary"
          value={t('runJob')}
          type="button"
          action={() => runJob()}
          disabled={!selectedJob || selectedJob?.state === 'PAUSED' || !hasPermissions}
        />
      </Buttons>
      {jobs.length ? (
        <Table
          id="timers-table"
          rowId="name"
          hasToolbar
          columns={columns}
          rows={jobs}
          onSelection={onSelection}
          initialSelection={selectedJob?.name ? [selectedJob.name] : []}
          hasSelection
          hasPagination
          hasResize
          hasSort
          hasFilter
        />
      ) : (
        <div>{t('no_data')}</div>
      )}
    </Wrapper>
  );
};

export default Timers;
